import { useState } from "react";
import { motion } from "framer-motion";
import { Footprints, Bike, Ruler, Clock, Mountain, MapPin, CloudRain, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Chapter";
import { RANDOS, BOOKING_URL } from "../lib/data";

const LEVEL_STYLE = {
  Facile: "bg-pine text-sand",
  Modéré: "bg-brun text-ink",
  Difficile: "bg-terra text-sand",
};

const typeIcon = (t) => (t.toLowerCase().includes("vélo") || t.toLowerCase().includes("vtt") ? Bike : Footprints);

export default function RandonneesPage() {
  const [selected, setSelected] = useState(2);

  const showOnMap = (i) => {
    setSelected(i);
    document.getElementById("rando-map")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <main data-testid="page-randonnees" className="grain">
      <section className="relative pt-40 pb-14 px-6 sm:px-10 lg:px-16 bg-cream overflow-hidden">
        <Footprints className="absolute -right-8 top-20 w-64 h-64 text-pine/5 rotate-12" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-body text-xs font-bold tracking-[0.25em] uppercase text-terra">
              Autour du camping · Balades & nature
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
              Les 7 plus belles <span className="text-pine">randonnées</span> autour du camping
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-ink/65 text-base lg:text-lg leading-relaxed">
              Itinéraires, durées et niveaux : de la boucle famille d'1,6 km à la grande
              boucle de 21 km autour du lac de Bannac, tous les circuits comparent en un
              seul coup d'œil — et se situent sur la carte.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="rando-legend">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-ink/40 self-center mr-2">Niveaux :</span>
              {Object.keys(LEVEL_STYLE).map((l) => (
                <span key={l} className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${LEVEL_STYLE[l]}`}>{l}</span>
              ))}
              <span className="rounded-full px-3.5 py-1.5 text-xs font-semibold bg-sand border border-ink/10 text-ink/55">
                7 circuits · de 1,6 à 21 km
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6">
            {RANDOS.map((r, i) => {
              const TypeIcon = typeIcon(r.type);
              const active = selected === i;
              return (
                <Reveal key={r.name} delay={0.05 * i}>
                  <article
                    className={`rounded-[1.75rem] bg-sand border p-6 sm:p-8 transition-all duration-300 ${
                      active ? "border-pine/50 shadow-xl shadow-pine/10" : "border-ink/10 hover:border-pine/30 hover:shadow-lg"
                    }`}
                    data-testid={`rando-card-${i}`}
                  >
                    <div className="flex items-start gap-5">
                      <span className="font-display font-black text-4xl sm:text-5xl text-terra/80 leading-none shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-display font-bold text-2xl text-ink leading-tight">{r.name}</h2>
                          <span className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-wide uppercase ${LEVEL_STYLE[r.level]}`}>
                            {r.level}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-ink/55 font-medium">
                          <span className="inline-flex items-center gap-1.5"><Ruler size={14} className="text-pine" /> {r.distance}</span>
                          <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-pine" /> {r.duration}</span>
                          <span className="inline-flex items-center gap-1.5"><Mountain size={14} className="text-pine" /> {r.elevation}</span>
                          <span className="inline-flex items-center gap-1.5"><TypeIcon size={14} className="text-pine" /> {r.type}</span>
                        </div>
                        <p className="mt-4 text-sm text-ink/65 leading-relaxed">{r.highlights}</p>
                        <p className="mt-2 text-xs text-ink/45">
                          <b className="text-ink/60">Idéal pour :</b> {r.ideal}
                        </p>
                        <button
                          onClick={() => showOnMap(i)}
                          data-testid={`rando-map-btn-${i}`}
                          className={`mt-5 inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-all duration-300 ${
                            active ? "bg-pine text-sand shadow-md shadow-pine/25" : "bg-cream border border-ink/10 text-ink/70 hover:border-pine/40 hover:text-pine"
                          }`}
                        >
                          <MapPin size={15} /> Voir sur la carte
                        </button>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <Reveal delay={0.15}>
              <div id="rando-map" className="rounded-[1.75rem] overflow-hidden border-8 border-sand shadow-2xl shadow-ink/15 bg-sand" data-testid="rando-map-frame">
                <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink/40">Secteur du circuit</div>
                    <div className="font-display font-bold text-lg text-ink truncate">{RANDOS[selected].name}</div>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${LEVEL_STYLE[RANDOS[selected].level]}`}>
                    {RANDOS[selected].level}
                  </span>
                </div>
                <motion.iframe
                  key={RANDOS[selected].mapQuery}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  title={`Carte — ${RANDOS[selected].name}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(RANDOS[selected].mapQuery)}&z=13&output=embed`}
                  className="w-full h-80 sm:h-96 border-0"
                  loading="lazy"
                />
                <p className="px-5 py-3 text-xs text-ink/40">Localisation indicative — demandez le tracé détaillé à l'accueil du camping.</p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="rounded-[1.75rem] bg-brun/25 border border-brun/40 p-6" data-testid="rando-advice">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-terra text-sand">
                    <CloudRain size={18} />
                  </span>
                  <h3 className="font-display font-bold text-xl text-ink">Conseil pratique</h3>
                </div>
                <p className="mt-4 text-sm text-ink/70 leading-relaxed">
                  Après plusieurs journées de pluie, les bords du lac de Bannac deviennent très
                  marécageux — venez bien équipés dans ce cas. Pour les circuits sans balisage
                  comme la boucle d'Elbes, l'appli <b>Visorando</b> ou <b>SityTrail</b> est
                  recommandée pour suivre le tracé GPS.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rando-cta-booking"
                className="flex items-center justify-between gap-4 rounded-[1.75rem] bg-pine text-sand p-6 transition-all duration-300 hover:bg-pinedark hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/25"
              >
                <div>
                  <div className="font-display font-bold text-2xl">Envie de marcher ?</div>
                  <p className="mt-1 text-sm text-sand/70">Dormez à 2 pas des départs de rando, au bord du lac.</p>
                </div>
                <span className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-gold text-pinedark">
                  <ArrowUpRight size={20} />
                </span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
